import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import "./styles/Grapheachday.css";

ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
);

export default function CompareSitesLineChart() {
  const [masterSites, setMasterSites] = useState([]);
  const [selectedMasterSiteId, setSelectedMasterSiteId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [dataBySite, setDataBySite] = useState({});
  const [dates, setDates] = useState([]);
  const [activeTab, setActiveTab] = useState("water");
  const [loading, setLoading] = useState(false);
  const [colorMap, setColorMap] = useState({});

  useEffect(() => {
    const token = localStorage.getItem("token");
    axios
      .get("/api/mastersites", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setMasterSites(res.data);
        if (res.data.length > 0) {
          setSelectedMasterSiteId(res.data[0]._id);
        }
      })
      .catch((err) => {
        alert("❌ Failed to fetch MasterSites");
        console.error(err);
      });
  }, []);

  const fetchComparisonData = async () => {
    if (!selectedMasterSiteId || !fromDate || !toDate) {
      alert("⚠️ Please select all fields.");
      return;
    }

    const start = new Date(fromDate);
    const end = new Date(toDate);

    if (start > end) {
      alert("⚠️ Start date cannot be after end date.");
      return;
    }

    const diffDays = (end - start) / (1000 * 60 * 60 * 24);
    if (diffDays > 31) {
      alert("⚠️ Date range cannot exceed 31 days.");
      return;
    }

    const format = (d) => {
      const dd = new Date(d);
      return `${String(dd.getDate()).padStart(2, "0")}/${String(
        dd.getMonth() + 1
      ).padStart(2, "0")}/${dd.getFullYear()}`;
    };

    const fromFormatted = format(fromDate);
    const toFormatted = format(toDate);
    const token = localStorage.getItem("token");

    setLoading(true);
    try {
      const res = await axios.get(
        `/api/graphday/compare/master/${selectedMasterSiteId}?from=${fromFormatted}&to=${toFormatted}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const structured = {};
      const uniqueDates = new Set();

      for (const row of res.data) {
        const { site, date, totalWaterDispensed, totalAmountCollected } = row;
        uniqueDates.add(date);
        if (!structured[site]) structured[site] = {};
        structured[site][date] = {
          water: totalWaterDispensed,
          amount: totalAmountCollected,
        };
      }

      const sortedDates = Array.from(uniqueDates).sort((a, b) => {
        const da = new Date(a.split("/").reverse().join("-"));
        const db = new Date(b.split("/").reverse().join("-"));
        return da - db;
      });

      // Generate colors for sites once after fetching
      const siteNames = Object.keys(structured);
      const generateColor = (i) => {
        const hue = (i * 137.5) % 360;
        return `hsl(${hue}, 70%, 50%)`;
      };
      const colorMapping = {};
      siteNames.forEach((site, index) => {
        colorMapping[site] = generateColor(index);
      });

      setDates(sortedDates);
      setDataBySite(structured);
      setColorMap(colorMapping);
    } catch (err) {
      alert("❌ Error fetching comparison data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const generateChartData = (type) => {
    const datasets = Object.entries(dataBySite).map(([site, entries]) => ({
      label: site,
      data: dates.map((date) => entries[date]?.[type] || 0),
      fill: false,
      tension: 0.3,
      borderColor: colorMap[site] || "#000",
      backgroundColor: colorMap[site] || "#000",
      borderWidth: 2,
      pointRadius: 2,
    }));

    return {
      labels: dates,
      datasets,
    };
  };

  return (
    <div className="compare-sites-container1">
      <h2>📈 Daily Comparison by Site</h2>

      <div className="compare-filters">
        <select
          className="input-control"
          value={selectedMasterSiteId}
          onChange={(e) => setSelectedMasterSiteId(e.target.value)}
        >
          {masterSites.map((s) => (
            <option key={s._id} value={s._id}>
              {s.name}
            </option>
          ))}
        </select>

        <input
          type="date"
          className="input-control"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
        />
        <input
          type="date"
          className="input-control"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
        />

        <button className="btn fetch" onClick={fetchComparisonData}>
          Fetch Data
        </button>
      </div>

      <div className="tab-controls">
        <button
          className={`tab-btn ${activeTab === "water" ? "active" : ""}`}
          onClick={() => setActiveTab("water")}
        >
          💧 Water Dispensed
        </button>
        <button
          className={`tab-btn ${activeTab === "amount" ? "active" : ""}`}
          onClick={() => setActiveTab("amount")}
        >
          💰 Amount Collected
        </button>
      </div>

      {loading ? (
        <p className="loading">⏳ Loading...</p>
      ) : dates.length === 0 ? (
        <p className="no-data">No data to display. Select range and fetch.</p>
      ) : (
        <>
          <div className="chart-box">
            <Line
              data={generateChartData(activeTab === "water" ? "water" : "amount")}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  title: {
                    display: true,
                    text:
                      activeTab === "water"
                        ? "Daily Water Dispensed per Site"
                        : "Daily Amount Collected per Site",
                  },
                },
              }}
              height={400}
            />
          </div>

          <div className="custom-legend">
            {Object.entries(colorMap).map(([site, color]) => (
              <span key={site} className="legend-tag" style={{ backgroundColor: color }}>
                {site}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
